import React, { useState, useEffect, useCallback, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import {
    Card,
    DatePicker,
    Button,
    Space,
    Typography,
    Spin,
    Empty,
    Tag,
    Tooltip,
    Modal,
    Form,
    Input,
    Select,
    Table,
    App,
    Popconfirm,
    Popover,
    Divider,
} from "antd";
import {
    RefreshCw,
    TrendingUp,
    Wallet,
    ArrowUpRight,
    ArrowDownRight,
    Shield,
    BarChart3,
    Plus,
    Trash2,
    MoreHorizontal,
    Info,
    Target,
    Activity,
    Pencil,
} from "lucide-react";
import dayjs from "dayjs";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import { formatChartTooltipValue, formatVolume } from "../../utils/formatters";
import { buildMultiGroupVolumeTrendOption } from "../../utils/hedgePanoramaCharts";
import "./HedgeMonitor.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const QUICK_DATES = [
    { label: "7天", value: () => [dayjs().subtract(7, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
    { label: "30天", value: () => [dayjs().subtract(30, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
    { label: "本月", value: () => [dayjs().startOf("month"), dayjs()] },
    { label: "上月", value: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
];

/** 主布局在 .main-content 内滚动（非 window）；Tooltip 挂到此处才能与触发器坐标一致 */
const hedgePopupContainer = (triggerNode) =>
    triggerNode?.closest?.(".main-content") ?? document.body;

const HedgeMonitor = () => {
    const { isDark } = useTheme();
    const { message: msg, modal } = App.useApp();
    const [loading, setLoading] = useState(false);

    // Groups
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [trendsData, setTrendsData] = useState(null);

    // Filters
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(30, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
    ]);
    const [activeQuickDate, setActiveQuickDate] = useState("30天");

    // Create Modal
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [form] = Form.useForm();

    // Edit Modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editForm] = Form.useForm();

    // Available accounts for selection
    const [availableAccounts, setAvailableAccounts] = useState([]);

    const fetchGroups = useCallback(async () => {
        try {
            const res = await apiService.hedgeGroups.getGroups();
            if (res.data?.success) {
                setGroups(res.data.data || []);
                // Auto-select first group
                if (!selectedGroupId && res.data.data?.length > 0) {
                    setSelectedGroupId(res.data.data[0].id);
                }
            }
        } catch (err) {
            console.error("Fetch groups error:", err);
        }
    }, [selectedGroupId]);

    const fetchTrends = useCallback(async () => {
        if (!selectedGroupId) return;
        setLoading(true);
        try {
            const params = {
                date_from: dateRange[0].format("YYYY-MM-DD"),
                date_to: dateRange[1].format("YYYY-MM-DD"),
            };
            const res = await apiService.hedgeGroups.getGroupTrends(selectedGroupId, params);
            if (res.data?.success) {
                setTrendsData(res.data.data);
            }
        } catch (err) {
            console.error("Fetch trends error:", err);
            msg.error("获取走势数据失败");
        } finally {
            setLoading(false);
        }
    }, [selectedGroupId, dateRange, msg]);

    const fetchAvailableAccounts = useCallback(async () => {
        try {
            const res = await apiService.accountConfigs.getConfigs({ page_size: 100 });
            if (res.data?.success) {
                setAvailableAccounts(res.data.data?.list || []);
            }
        } catch (err) {
            console.error("Fetch accounts error:", err);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
        fetchAvailableAccounts();
    }, [fetchGroups, fetchAvailableAccounts]);

    useEffect(() => {
        fetchTrends();
    }, [fetchTrends]);

    const handleQuickDate = (label) => {
        const option = QUICK_DATES.find(d => d.label === label);
        if (option) {
            setDateRange(option.value());
            setActiveQuickDate(label);
        }
    };

    const handleCreateGroup = async (values) => {
        setCreateLoading(true);
        try {
            const memberA = availableAccounts.find(a => a.id === values.member_a);
            const memberB = availableAccounts.find(a => a.id === values.member_b);
            if (!memberA || !memberB) {
                msg.error("请选择有效的账号");
                return;
            }

            const data = {
                name: values.name,
                description: values.description || "",
                members: [
                    { account: memberA.account, exchange: memberA.exchange, trade_type: memberA.trade_type, role: "A" },
                    { account: memberB.account, exchange: memberB.exchange, trade_type: memberB.trade_type, role: "B" },
                ],
            };

            const res = await apiService.hedgeGroups.createGroup(data);
            if (res.data?.success) {
                msg.success("对冲组创建成功");
                setCreateModalOpen(false);
                form.resetFields();
                fetchGroups();
                setSelectedGroupId(res.data.data.id);
            }
        } catch (err) {
            msg.error("创建失败: " + (err.apiMessage || err.message));
        } finally {
            setCreateLoading(false);
        }
    };

    const openEditModal = (g) => {
        setEditingGroup(g);
        const memberA = g.members?.find((m) => m.role === "A");
        const memberB = g.members?.find((m) => m.role === "B");
        const matchId = (m) => {
            if (!m) return undefined;
            const ac = availableAccounts.find(
                (a) =>
                    a.account === m.account &&
                    a.exchange === m.exchange &&
                    a.trade_type === m.trade_type,
            );
            return ac?.id;
        };
        editForm.setFieldsValue({
            name: g.name,
            description: g.description ?? "",
            member_a: matchId(memberA),
            member_b: matchId(memberB),
        });
        setEditModalOpen(true);
    };

    const handleUpdateGroup = async (values) => {
        if (!editingGroup?.id) return;
        setEditLoading(true);
        try {
            const memberA = availableAccounts.find((a) => a.id === values.member_a);
            const memberB = availableAccounts.find((a) => a.id === values.member_b);
            if (!memberA || !memberB) {
                msg.error("请选择有效的账号");
                return;
            }

            const data = {
                name: values.name,
                description: values.description || "",
                members: [
                    { account: memberA.account, exchange: memberA.exchange, trade_type: memberA.trade_type, role: "A" },
                    { account: memberB.account, exchange: memberB.exchange, trade_type: memberB.trade_type, role: "B" },
                ],
            };

            const res = await apiService.hedgeGroups.updateGroup(editingGroup.id, data);
            if (res.data?.success) {
                msg.success("对冲组已更新");
                setEditModalOpen(false);
                setEditingGroup(null);
                editForm.resetFields();
                await fetchGroups();
                if (selectedGroupId === editingGroup.id) {
                    fetchTrends();
                }
            }
        } catch (err) {
            msg.error("更新失败: " + (err.apiMessage || err.message));
        } finally {
            setEditLoading(false);
        }
    };

    const handleDeleteGroup = async (id) => {
        try {
            await apiService.hedgeGroups.deleteGroup(id);
            msg.success("已删除");
            if (selectedGroupId === id) {
                setSelectedGroupId(null);
                setTrendsData(null);
            }
            fetchGroups();
        } catch (err) {
            msg.error("删除失败");
        }
    };

    const analysis = trendsData?.analysis;
    const combined = trendsData?.combined || [];
    const members = trendsData?.members || [];

    const latestNetAssets = combined.length > 0 ? (combined[combined.length - 1]?.net_assets || 0) : 0;

    // ECharts option — 3 lines + PnL bar
    const chartOption = useMemo(() => {
        if (!combined.length) return {};
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
        const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0,0,0,0.05)";

        const dates = combined.map(c => dayjs(c.date).format("MM-DD"));
        const combinedNA = combined.map(c => c.net_assets);
        const combinedPnL = combined.map(c => c.pnl);

        const seriesLines = [
            {
                name: "组合净资产",
                type: "line",
                data: combinedNA,
                smooth: true,
                lineStyle: { width: 3, color: "#1890ff" },
                itemStyle: { color: "#1890ff" },
                symbol: "circle",
                symbolSize: 6,
                label: {
                    show: true,
                    position: "top",
                    color: textColor,
                    fontSize: 10,
                    distance: 10,
                    padding: [4, 4],
                    backgroundColor: isDark ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.6)",
                    borderRadius: 4,
                    formatter: (p) => p.value ? Math.round(p.value).toLocaleString() : "",
                },
                areaStyle: {
                    color: { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: "rgba(24,144,255,0.15)" }, { offset: 1, color: "rgba(24,144,255,0)" }] }
                }
            }
        ];

        const MEMBER_COLORS = ["#f59e0b", "#a855f7"];
        const MEMBER_LABELS = ["账号A", "账号B"];

        members.forEach((m, idx) => {
            // Build date-aligned data
            const dateMap = {};
            m.trends.forEach(t => { dateMap[dayjs(t.date).format("MM-DD")] = t.net_assets; });
            const aligned = dates.map(d => dateMap[d] ?? null);

            seriesLines.push({
                name: `${MEMBER_LABELS[idx]}: ${m.account}`,
                type: "line",
                data: aligned,
                smooth: true,
                lineStyle: { width: 2, color: MEMBER_COLORS[idx], type: "dashed" },
                itemStyle: { color: MEMBER_COLORS[idx] },
                symbol: "diamond",
                symbolSize: 4,
            });
        });

        // Add PnL bar
        seriesLines.push({
            name: "组合盈亏",
            type: "bar",
            yAxisIndex: 1,
            data: combinedPnL,
            itemStyle: {
                color: (params) => params.value >= 0 ? "#52c41a" : "#f5222d",
                borderRadius: [4, 4, 0, 0],
            },
            label: {
                show: true,
                position: "inside",
                color: "#fff",
                fontSize: 10,
                fontWeight: "bold",
                formatter: (p) => p.value ? Math.round(p.value).toLocaleString() : "",
            },
            barMaxWidth: 30,
        });

        const legendData = seriesLines.map(s => s.name);

        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
                borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                textStyle: { color: isDark ? "#f0f0f5" : "#1f2937" },
                formatter: (params) => {
                    let res = `<div style="margin-bottom:4px;font-weight:600">${params[0].axisValue}</div>`;
                    params.forEach(item => {
                        const color = typeof item.color === 'object' ? '#1890ff' : item.color;
                        res += `<div style="display:flex;justify-content:space-between;gap:20px;align-items:center;">
                            <span><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;"></span>${item.seriesName}</span>
                            <span style="font-weight:bold">${item.value != null ? formatChartTooltipValue(item.value) : '—'}</span>
                        </div>`;
                    });
                    return res;
                }
            },
            legend: {
                bottom: 0,
                data: legendData,
                textStyle: { color: textColor, fontSize: 12 },
            },
            grid: {
                left: "3%", right: "4%", bottom: "15%", top: "15%", containLabel: true,
            },
            xAxis: {
                type: "category",
                data: dates,
                axisLabel: { color: textColor },
                axisLine: { lineStyle: { color: isDark ? "rgba(255, 255, 255, 0.15)" : "#e2e8f0" } },
            },
            yAxis: [
                {
                    type: "value", name: "净资产",
                    nameTextStyle: { color: textColor }, scale: true,
                    axisLabel: { color: textColor },
                    splitLine: { lineStyle: { type: "dashed", color: splitLineColor } },
                },
                {
                    type: "value", name: "盈亏",
                    nameTextStyle: { color: textColor }, scale: true,
                    axisLabel: { color: textColor },
                    splitLine: { show: false },
                },
            ],
            series: seriesLines,
        };
    }, [combined, members, isDark]);

    // 逐日明细：百分比列宽 + fixed 布局，适配卡片宽度，避免整表左右滑动
    const tableColumns = useMemo(() => {
        const hdr = (full, short) => (
            <Tooltip title={full} mouseEnterDelay={0.35} getPopupContainer={hedgePopupContainer}>
                <span>{short}</span>
            </Tooltip>
        );

        const fmt = (v) => v?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? "-";
        const fmtPnl = (v, bold) =>
            v != null ? (
                <span style={{ color: v >= 0 ? "#10b981" : "#ef4444", fontWeight: bold ? 700 : 600 }}>
                    {v >= 0 ? "+" : ""}
                    {v.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </span>
            ) : "-";

        if (members.length < 2) {
            return [
                { title: "日期", dataIndex: "date", key: "date", width: "12%", render: (v) => dayjs(v).format("MM-DD") },
                { title: hdr("组合净资产", "组合净资"), dataIndex: "combined_na", key: "combined_na", width: "38%", align: "right", render: (v) => <strong>{fmt(v)}</strong> },
                { title: hdr("组合盈亏", "组合盈亏"), dataIndex: "combined_pnl", key: "combined_pnl", width: "25%", align: "right", render: (v) => fmtPnl(v, true) },
                {
                    title: hdr("对冲效率", "效率"),
                    dataIndex: "hedge_eff",
                    key: "hedge_eff",
                    width: "25%",
                    align: "right",
                    render: (v) => (v != null ? <Tag color={v >= 50 ? "green" : v >= 0 ? "orange" : "red"} style={{ margin: 0, fontSize: 11, lineHeight: "18px", padding: "0 5px" }}>{v.toFixed(1)}%</Tag> : "-"),
                },
            ];
        }

        return [
            { title: "日期", dataIndex: "date", key: "date", width: "7%", render: (v) => dayjs(v).format("MM-DD") },
            { title: hdr("A净资产", "A净资"), dataIndex: "a_na", key: "a_na", width: "13%", align: "right", render: fmt },
            { title: hdr("A盈亏", "A盈亏"), dataIndex: "a_pnl", key: "a_pnl", width: "10%", align: "right", render: (v) => fmtPnl(v, false) },
            { title: hdr("B净资产", "B净资"), dataIndex: "b_na", key: "b_na", width: "13%", align: "right", render: fmt },
            { title: hdr("B盈亏", "B盈亏"), dataIndex: "b_pnl", key: "b_pnl", width: "10%", align: "right", render: (v) => fmtPnl(v, false) },
            { title: hdr("组合净资产", "组合净资"), dataIndex: "combined_na", key: "combined_na", width: "17%", align: "right", render: (v) => <strong>{fmt(v)}</strong> },
            { title: hdr("组合盈亏", "组合盈亏"), dataIndex: "combined_pnl", key: "combined_pnl", width: "10%", align: "right", render: (v) => fmtPnl(v, true) },
            {
                title: hdr("对冲效率", "效率"),
                dataIndex: "hedge_eff",
                key: "hedge_eff",
                width: "10%",
                align: "right",
                render: (v) => (v != null ? <Tag color={v >= 50 ? "green" : v >= 0 ? "orange" : "red"} style={{ margin: 0, fontSize: 11, lineHeight: "18px", padding: "0 5px" }}>{v.toFixed(1)}%</Tag> : "-"),
            },
        ];
    }, [members]);

    // Build table data
    const tableData = useMemo(() => {
        return combined.map((c, i) => {
            const row = {
                key: c.date,
                date: c.date,
                combined_na: c.net_assets,
                combined_pnl: c.pnl,
            };

            if (members.length >= 2) {
                const aPoint = members[0].trends[i];
                const bPoint = members[1].trends[i];
                row.a_na = aPoint?.net_assets;
                row.a_pnl = aPoint?.pnl;
                row.b_na = bPoint?.net_assets;
                row.b_pnl = bPoint?.pnl;

                // Hedge efficiency
                const aPnl = aPoint?.pnl || 0;
                const bPnl = bPoint?.pnl || 0;
                const sumAbs = Math.abs(aPnl) + Math.abs(bPnl);
                row.hedge_eff = sumAbs > 0 ? (1 - Math.abs(c.pnl) / sumAbs) * 100 : null;
            }

            return row;
        });
    }, [combined, members]);

    const winPct = analysis ? (analysis.win_days + analysis.loss_days > 0 ? (analysis.win_days / (analysis.win_days + analysis.loss_days)) * 100 : 0) : 0;

    const volumeTrendLineOption = useMemo(() => {
        const vt = trendsData?.volume_trend;
        if (!vt?.length) return null;
        return buildMultiGroupVolumeTrendOption({
            seriesList: [
                {
                    group: { name: trendsData?.group?.name || "本对冲组" },
                    volumeTrend: vt,
                },
            ],
            isDark,
        });
    }, [trendsData, isDark]);

    return (
        <div className="hedge-monitor-container">
            {/* Header */}
            <div className="hedge-header">
                <div className="header-left">
                    <Title level={4} style={{ margin: 0, color: "inherit" }}>
                        对冲组监控
                        <Tooltip
                            title="将 2 个账号配对为对冲组，统一视角查看组合盈亏与对冲效率"
                            getPopupContainer={hedgePopupContainer}
                            placement="top"
                            arrow={{ pointAtCenter: true }}
                        >
                            <span style={{ display: "inline-flex", marginLeft: 8, cursor: "help", verticalAlign: "middle" }}>
                                <Info size={16} style={{ opacity: 0.45 }} />
                            </span>
                        </Tooltip>
                    </Title>
                    <Text type="secondary" style={{ color: isDark ? "rgba(255,255,255,0.45)" : "#64748b" }}>
                        组合净资产 · 区间总交易量（两成员成交量之和）· 对冲效率 · 盈亏分析
                    </Text>
                </div>
                <Space size="middle" className="header-controls">
                    <div className="quick-date-buttons">
                        {QUICK_DATES.map(item => (
                            <button key={item.label} className={`quick-date-btn ${activeQuickDate === item.label ? "active" : ""}`} onClick={() => handleQuickDate(item.label)}>
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <RangePicker value={dateRange} onChange={(dates) => { if (dates) setDateRange(dates); setActiveQuickDate(""); }} allowClear={false} />
                    <Button type="primary" icon={<RefreshCw size={14} />} onClick={fetchTrends} loading={loading}>刷新</Button>
                </Space>
            </div>

            {/* Group selector */}
            <div className="group-selector-section">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Shield size={18} style={{ color: isDark ? "#60a5fa" : "#3b82f6" }} />
                    <Text strong>对冲组:</Text>
                </div>
                <div className="group-tags">
                    {groups.map(g => (
                        <div
                            key={g.id}
                            className={`group-tag-chip ${selectedGroupId === g.id ? "active" : ""}`}
                        >
                            <button
                                type="button"
                                className="group-tag group-tag-label"
                                onClick={() => setSelectedGroupId(g.id)}
                            >
                                {g.name}
                            </button>
                            <Popover
                                trigger="click"
                                placement="bottomLeft"
                                getPopupContainer={hedgePopupContainer}
                                overlayClassName="hedge-group-popover-overlay"
                                content={(
                                    <div className="hedge-group-popover-inner">
                                        <Text strong className="hedge-group-popover-heading">组成员</Text>
                                        <div className="hedge-group-popover-members">
                                            {g.members?.map(m => (
                                                <div key={`${g.id}-${m.role}-${m.account}`} className="hedge-group-popover-row">
                                                    <Tag color={m.role === "A" ? "orange" : "cyan"}>{m.role}</Tag>
                                                    <span className="hedge-group-popover-account">{m.account}</span>
                                                    <Text type="secondary" className="hedge-group-popover-meta">
                                                        {m.exchange} / {m.trade_type}
                                                    </Text>
                                                </div>
                                            ))}
                                        </div>
                                        <Button
                                            type="default"
                                            size="small"
                                            icon={<Pencil size={14} />}
                                            block
                                            style={{ marginBottom: 8 }}
                                            onClick={() => openEditModal(g)}
                                        >
                                            编辑组名与账号
                                        </Button>
                                        <Divider style={{ margin: "12px 0" }} />
                                        <Popconfirm
                                            title="删除该对冲组？"
                                            description="删除后不可恢复，请确认无在用依赖。"
                                            okText="删除"
                                            cancelText="取消"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => handleDeleteGroup(g.id)}
                                            getPopupContainer={hedgePopupContainer}
                                        >
                                            <Button danger size="small" icon={<Trash2 size={14} />} block>
                                                删除此组
                                            </Button>
                                        </Popconfirm>
                                    </div>
                                )}
                            >
                                <button
                                    type="button"
                                    className="group-tag-more"
                                    aria-label={`${g.name} 详情与操作`}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <MoreHorizontal size={16} strokeWidth={2.25} />
                                </button>
                            </Popover>
                        </div>
                    ))}
                    <Button type="dashed" size="small" icon={<Plus size={14} />} onClick={() => setCreateModalOpen(true)} style={{ borderRadius: 20 }}>
                        新建对冲组
                    </Button>
                </div>
            </div>

            {groups.length === 0 && !loading ? (
                <div className="hedge-empty-state">
                    <Shield size={48} style={{ color: isDark ? "#475569" : "#94a3b8", marginBottom: 16 }} />
                    <Title level={5} style={{ marginBottom: 8 }}>暂无对冲组</Title>
                    <Text type="secondary">点击「新建对冲组」选择 2 个账号配对开始监控</Text>
                    <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreateModalOpen(true)} style={{ marginTop: 16 }}>
                        新建对冲组
                    </Button>
                </div>
            ) : (
                <Spin spinning={loading}>
                    {/* KPI Cards */}
                    {trendsData && (
                        <div className="hedge-stats-cards">
                            <Card className="hedge-stat-card blue-gradient" variant="borderless">
                                <div className="stat-row-top"><Wallet size={16} color="white" /><span className="stat-label">组合净资产</span></div>
                                <div className="stat-value">{latestNetAssets.toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small></div>
                            </Card>
                            <Card className="hedge-stat-card cyan-gradient" variant="borderless">
                                <div className="stat-row-top">
                                    <BarChart3 size={16} color="white" />
                                    <Tooltip
                                        title="所选日期范围内，组内两成员在 trade_volumes 中（账号+交易所+现货/合约）的成交量合计"
                                        getPopupContainer={hedgePopupContainer}
                                    >
                                        <span className="stat-label" style={{ cursor: "help", borderBottom: "1px dotted rgba(255,255,255,0.5)" }}>
                                            区间总交易量
                                        </span>
                                    </Tooltip>
                                </div>
                                <div className="stat-value">{formatVolume(analysis?.period_total_volume ?? 0)}</div>
                            </Card>
                            <Card className={`hedge-stat-card ${(analysis?.total_pnl || 0) >= 0 ? "green-gradient" : "red-gradient"}`} variant="borderless">
                                <div className="stat-row-top"><TrendingUp size={16} color="white" /><span className="stat-label">区间盈亏</span></div>
                                <div className="stat-value">
                                    {(analysis?.total_pnl || 0) >= 0 ? <ArrowUpRight size={16} color="white" /> : <ArrowDownRight size={16} color="white" />}
                                    {(analysis?.total_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small>
                                </div>
                            </Card>
                            <Card className="hedge-stat-card purple-gradient" variant="borderless">
                                <div className="stat-row-top"><Target size={16} color="white" /><span className="stat-label">对冲效率</span></div>
                                <div className="stat-value">{(analysis?.avg_hedge_efficiency || 0).toFixed(1)}<small>%</small></div>
                            </Card>
                            <Card className="hedge-stat-card orange-gradient" variant="borderless">
                                <div className="stat-row-top"><Activity size={16} color="white" /><span className="stat-label">最大回撤</span></div>
                                <div className="stat-value">-{(analysis?.max_drawdown_percent || 0).toFixed(2)}<small>%</small></div>
                            </Card>
                        </div>
                    )}

                    {trendsData && (
                        <Card
                            className="hedge-chart-card"
                            variant="borderless"
                            title={(
                                <Space>
                                    <BarChart3 size={16} />
                                    <span>每日总交易量走势</span>
                                    <Tooltip title="每个交易日为组内两成员在 trade_volumes 中的成交量之和；区间内无成交日记为 0" getPopupContainer={hedgePopupContainer}>
                                        <Info size={14} style={{ opacity: 0.45, cursor: "help" }} />
                                    </Tooltip>
                                </Space>
                            )}
                        >
                            {volumeTrendLineOption ? (
                                <ReactECharts option={volumeTrendLineOption} style={{ height: 320, width: "100%" }} />
                            ) : (
                                <Empty description="所选区间内暂无成交量数据" style={{ margin: "32px 0" }} />
                            )}
                        </Card>
                    )}

                    {/* Main Chart */}
                    {combined.length > 0 ? (
                        <Card className="hedge-chart-card" variant="borderless">
                            <ReactECharts option={chartOption} style={{ height: "450px" }} />
                        </Card>
                    ) : (
                        !loading && trendsData && <Empty description="所选区间内暂无数据" />
                    )}

                    {/* Analysis + Table */}
                    {trendsData && combined.length > 0 && (
                        <div className="hedge-analysis-section">
                            {/* PnL Analysis */}
                            <Card title={<Space><BarChart3 size={16} /><span>盈亏分析</span></Space>} className="analysis-card" variant="borderless">
                                <div className="pnl-analysis-box">
                                    <div className="pnl-row">
                                        <span className="pnl-label">盈利天数</span>
                                        <span className="pnl-value positive">{analysis?.win_days || 0} 天</span>
                                    </div>
                                    <div className="pnl-row">
                                        <span className="pnl-label">亏损天数</span>
                                        <span className="pnl-value negative">{analysis?.loss_days || 0} 天</span>
                                    </div>
                                    <div className="pnl-row">
                                        <span className="pnl-label">胜率</span>
                                        <span className="pnl-value">{(analysis?.win_rate || 0).toFixed(1)}%</span>
                                    </div>
                                    <div className="pnl-row">
                                        <span className="pnl-label">盈利总额</span>
                                        <span className="pnl-value positive">+{(analysis?.total_profit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="pnl-row">
                                        <span className="pnl-label">亏损总额</span>
                                        <span className="pnl-value negative">{(analysis?.total_loss || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                                    </div>
                                    <div className="pnl-row">
                                        <span className="pnl-label">盈亏比</span>
                                        <span className="pnl-value">
                                            {(analysis?.profit_loss_ratio || 0).toFixed(2)} : 1
                                            {(analysis?.profit_loss_ratio || 0) >= 1.5 ? " ✅" : (analysis?.profit_loss_ratio || 0) >= 1 ? " ⚠️" : " ❌"}
                                        </span>
                                    </div>
                                    <div className="pnl-progress-bar">
                                        <div className="pnl-progress-win" style={{ width: `${winPct}%` }}>
                                            盈{winPct.toFixed(0)}%
                                        </div>
                                        <div className="pnl-progress-loss" style={{ width: `${100 - winPct}%` }}>
                                            亏{(100 - winPct).toFixed(0)}%
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* Detail Table */}
                            <Card title={<Space><TrendingUp size={16} /><span>逐日明细</span></Space>} className="analysis-card" variant="borderless">
                                <Table
                                    className="hedge-table hedge-table--fit"
                                    tableLayout="fixed"
                                    dataSource={tableData}
                                    columns={tableColumns}
                                    size="small"
                                    pagination={{ pageSize: 15, showSizeChanger: false }}
                                    scroll={{ y: 400 }}
                                />
                            </Card>
                        </div>
                    )}
                </Spin>
            )}

            {/* Create Modal */}
            <Modal
                title="新建对冲组"
                open={createModalOpen}
                onCancel={() => setCreateModalOpen(false)}
                onOk={() => form.submit()}
                confirmLoading={createLoading}
                className="hedge-create-modal"
                width={520}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateGroup}>
                    <Form.Item name="name" label="组名" rules={[{ required: true, message: "请输入组名" }]}>
                        <Input placeholder="如: BTC对冲组A" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea placeholder="可选备注" rows={2} />
                    </Form.Item>
                    <Form.Item name="member_a" label="账号 A" rules={[{ required: true, message: "请选择账号A" }]}>
                        <Select
                            placeholder="选择第一个账号"
                            showSearch
                            optionFilterProp="label"
                            options={availableAccounts.map(a => ({
                                value: a.id,
                                label: `${a.account} (${a.exchange} / ${a.trade_type})`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="member_b" label="账号 B" rules={[{ required: true, message: "请选择账号B" }]}>
                        <Select
                            placeholder="选择第二个账号"
                            showSearch
                            optionFilterProp="label"
                            options={availableAccounts.map(a => ({
                                value: a.id,
                                label: `${a.account} (${a.exchange} / ${a.trade_type})`,
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="编辑对冲组"
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    setEditingGroup(null);
                    editForm.resetFields();
                }}
                onOk={() => editForm.submit()}
                confirmLoading={editLoading}
                className="hedge-create-modal"
                width={520}
            >
                <Form form={editForm} layout="vertical" onFinish={handleUpdateGroup}>
                    <Form.Item name="name" label="组名" rules={[{ required: true, message: "请输入组名" }]}>
                        <Input placeholder="如: BTC对冲组A" />
                    </Form.Item>
                    <Form.Item name="description" label="描述">
                        <Input.TextArea placeholder="可选备注" rows={2} />
                    </Form.Item>
                    <Form.Item name="member_a" label="账号 A" rules={[{ required: true, message: "请选择账号A" }]}>
                        <Select
                            placeholder="选择第一个账号"
                            showSearch
                            optionFilterProp="label"
                            options={availableAccounts.map(a => ({
                                value: a.id,
                                label: `${a.account} (${a.exchange} / ${a.trade_type})`,
                            }))}
                        />
                    </Form.Item>
                    <Form.Item name="member_b" label="账号 B" rules={[{ required: true, message: "请选择账号B" }]}>
                        <Select
                            placeholder="选择第二个账号"
                            showSearch
                            optionFilterProp="label"
                            options={availableAccounts.map(a => ({
                                value: a.id,
                                label: `${a.account} (${a.exchange} / ${a.trade_type})`,
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default HedgeMonitor;
